/**
 * ADMIN-EDIT-01 — equipment categories, self-serve (Seth 2026-06-10: "I need
 * to run Pegasus without constant tweakings from you"). The table extends the
 * classifier's vocabulary: builtins (key immutable, label editable, never
 * archived — the rule classifier still emits them) + admin-added customs
 * (add / rename / regroup / archive). Active categories feed the
 * Classification Review picker; the runtime registry resolves labels +
 * system/auxiliary grouping everywhere equipment renders.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { setCustomEquipmentCategories } from "@/lib/equipment";

export interface EquipmentCategoryRow {
  key: string;
  label: string;
  grouping: "system" | "auxiliary" | "other";
  builtin: boolean;
  active: boolean;
  position: number;
  /** SKILL-STEER-01: the specialty (skill_domain id) a call about this kind of
   *  unit needs — a mini-split call needs Mini-Split. Null = none. */
  needs_skill_id: number | null;
  /** SKILL-STEER-01: techs offered first for it ("rooftop jobs → Camron first"). */
  offer_first: string[];
  /** REFRIG-01: installers log the refrigerant charge on this equipment (and what came out of the old one). */
  uses_refrigerant: boolean;
}

export function useEquipmentCategories() {
  return useQuery({
    queryKey: ["equipment_categories"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EquipmentCategoryRow[]> => {
      const { data, error } = await supabase
        .from("equipment_category")
        .select("*")
        .order("position")
        .order("label");
      if (error) throw error;
      return (data ?? []) as EquipmentCategoryRow[];
    },
  });
}

/** Push admin-added categories into the lib registry so labels/grouping
 *  resolve in the classifier wherever equipment renders. Mount once (Layout). */
export function useSyncEquipmentCategories() {
  const { data } = useEquipmentCategories();
  useEffect(() => {
    if (!data) return;
    setCustomEquipmentCategories(
      data.filter((c) => !c.builtin).map((c) => ({ key: c.key, label: c.label, grouping: c.grouping })),
    );
  }, [data]);
}

export function useAddEquipmentCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { label: string; grouping: EquipmentCategoryRow["grouping"] }) => {
      const key = v.label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      if (!key) throw new Error("Name the category first");
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("equipment_category").insert({
        key,
        label: v.label.trim(),
        grouping: v.grouping,
        builtin: false,
        created_by: u?.user?.email ?? null,
      });
      if (error) {
        if (error.message.includes("duplicate"))
          throw new Error("A category with that name already exists");
        throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment_categories"] }),
  });
}

export function useUpdateEquipmentCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      key: string;
      label?: string;
      grouping?: EquipmentCategoryRow["grouping"];
      active?: boolean;
      needs_skill_id?: number | null;
      offer_first?: string[];
      uses_refrigerant?: boolean;
    }) => {
      const { key, ...patch } = v;
      const { error } = await supabase.from("equipment_category").update(patch).eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["equipment_categories"] });
      void qc.invalidateQueries({ queryKey: ["refrigerant_keys"] });
    },
  });
}
